import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Autocomplete,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Delete, InfoOutlined } from "@mui/icons-material";
import { Formik, Form, Field, FieldArray, FormikHelpers } from "formik";
import * as Yup from "yup";

import NavigationHeader from "../../../components/NavigationHeader";
import { useToast } from "../../../components/Toast";
import { Loading } from "../../../components/Loading";
import {
  ContractFormData,
  CreateContractRequest,
  UpdateContractRequest,
  Contract,
  ContractAdditional,
  FetchedContract,
  ContractProfitSharing,
  ContractFuelConfig,
  ContractFixedBilling,
} from "../types/contract";
import {
  createContract,
  updateContract,
  getContractById,
} from "../services/contractApiService";
import { PROJECT_TERMINOLOGY, ProjectTabEnum } from "../enums";
import { commonButtonProps, smallButtonProp } from "../../../utils/styles";
import {
  Category,
  ContractStatus,
  ResourceType,
  RateType,
  BillingCycle,
  ProfitCalculationMethod,
  ContractType,
  AdditionalType,
  PricingType,
  VehicleCategory,
  getVehicleModels,
  getVehicleMakes,
  BillingType,
  LimitType,
} from "../enums/contractEnums";
import { commonTextFieldProps, smallButtonProps } from "../../../utils/styles";
import { ProjectModule } from "../types";
import ProjectBasicDetailView from "../components/ProjectBasicDetailView";
import { DocumentUploadSection } from "../../../components/DocumentUploadSection";
import { uploadFile } from "../../../utils/UploadFile";
import { projectModuleApi } from "../services/apiService";
import { formatCurrency } from "../../../utils/utils";

export const generateContractNumber = (name: string): string => {
  const now = new Date();
  const month = now.toLocaleString("default", { month: "short" }).toUpperCase();
  const year = now.getFullYear();

  const prefix = name.slice(0, 3).toUpperCase().padEnd(3, "X"); // pads with "X" if < 3 letters

  return `${prefix}-${month}-${year}`;
};

// Helper function to format labor type for display
export const formatLaborType = (laborType: string): string => {
  if (!laborType) return "";

  // Handle predefined enum values
  if (Object.values(ResourceType).includes(laborType as ResourceType)) {
    return laborType;
  }

  // Handle custom values - convert from SNAKE_CASE to Title Case
  return laborType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to get available contract types based on main contract type
export const getAvailableContractTypes = (contractType: ContractType) => {
  switch (contractType) {
    case ContractType.VEHICLE_ONLY:
      return [
        Category.SELECT_CATEGORY,
        Category.FIXED_BILLING,
        Category.FIXED,
        Category.PER_KM,
        Category.PER_SEAT,
        Category.ROUTE_BASED,
      ];
    case ContractType.VEHICLE_INCL_LABOR:
      return [
        Category.SELECT_CATEGORY,
        Category.FIXED_BILLING,
        Category.FIXED,
        Category.PER_KM,
        Category.PER_SEAT,
        Category.ROUTE_BASED,
      ];
    default:
      return [
        Category.SELECT_CATEGORY,
        Category.FIXED_BILLING,
        Category.FIXED,
        Category.PER_KM,
        Category.PER_SEAT,
        Category.ROUTE_BASED,
      ];
  }
};

// Validation schema
const validationSchema = Yup.object().shape({
  basicInfo: Yup.object().shape({
    contractNumber: Yup.string().required(
      `${ProjectTabEnum.CONTRACT.label} number is required`
    ),
    contractType: Yup.string().required(
      `${ProjectTabEnum.CONTRACT.label} type is required`
    ),
    billingCycle: Yup.string().required("Billing cycle is required"),
    category: Yup.string().required("Category is required"),
    startDate: Yup.string().required("Start date is required"),
    endDate: Yup.string().optional(),
    notes: Yup.string().optional(),
  }),
  // Per KM Terms validation
  perKmTerms: Yup.object().when("basicInfo.category", {
    is: Category.PER_KM,
    then: (schema) =>
      schema.shape({
        kmLimit: Yup.number()
          .min(1, "KM limit must be at least 1")
          .required("KM limit is required"),
        limitType: Yup.string().required("Limit type is required"),
        ratePerKm: Yup.number()
          .min(0, "Rate per KM must be non-negative")
          .required("Rate per KM is required"),
        overlimitRate: Yup.number()
          .min(0, "Over-limit rate must be non-negative")
          .required("Over-limit rate is required"),
      }),
    otherwise: (schema) => schema.optional(),
  }),
  // Per Seat Terms validation
  perSeatTerms: Yup.object().when("basicInfo.category", {
    is: Category.PER_SEAT,
    then: (schema) =>
      schema.shape({
        seatCount: Yup.number()
          .min(1, "Seat count must be at least 1")
          .required("Seat count is required"),
        ratePerSeat: Yup.number()
          .min(0, "Rate per seat must be non-negative")
          .required("Rate per seat is required"),
        minSeatsGuarantee: Yup.number()
          .min(0, "Minimum seats guarantee must be non-negative")
          .required("Minimum seats guarantee is required"),
        occupancyThreshold: Yup.number().optional(),
      }),
    otherwise: (schema) => schema.optional(),
  }),
  // Fixed Terms validation
  fixedTerms: Yup.object().when("basicInfo.category", {
    is: Category.FIXED,
    then: (schema) =>
      schema.shape({
        billingCycle: Yup.string().required("Billing cycle is required"),
        minGuaranteeAmount: Yup.number()
          .min(0, "Minimum guarantee amount must be non-negative")
          .required("Minimum guarantee amount is required"),
        distance: Yup.number()
          .min(0, "Distance must be non-negative")
          .required("Distance is required"),
      }),
    otherwise: (schema) => schema.optional(),
  }),
  // Resource Terms validation - required for VEHICLE_INCL_LABOR and LABOR_ONLY
  resourceTerms: Yup.array().when("basicInfo.contractType", {
    is: (contractType: ContractType) =>
      contractType === ContractType.VEHICLE_INCL_LABOR ||
      contractType === ContractType.LABOR_ONLY,
    then: (schema) =>
      schema
        .of(
          Yup.object().shape({
            resourceType: Yup.string().required("WorkForce type is required"),
            quantity: Yup.number()
              .min(1, "Count must be at least 1")
              .required("Count is required"),
            ratePerResource: Yup.string()
              .required("Rate per labor is required")
              .test("is-number", "Rate must be a valid number", (value) => {
                if (!value) return false;
                const num = parseFloat(value);
                return !isNaN(num) && num >= 0;
              }),
            rateType: Yup.string().required("Rate type is required"),
          })
        )
        .min(1, "At least one labor term is required"),
    otherwise: (schema) => schema.optional(),
  }),
  // Profit Sharing validation
  profitSharing: Yup.object().shape({
    profitSharingEnabled: Yup.boolean(),
    profitCalculationMethod: Yup.string().when("profitSharingEnabled", {
      is: true,
      then: (schema) =>
        schema.required("Profit calculation method is required"),
      otherwise: (schema) => schema.optional(),
    }),
    profitSharingPercentage: Yup.number().when("profitSharingEnabled", {
      is: true,
      then: (schema) =>
        schema
          .min(0, "Profit sharing percentage must be non-negative")
          .max(100, "Profit sharing percentage cannot exceed 100%")
          .required("Profit sharing percentage is required"),
      otherwise: (schema) => schema.optional(),
    }),
    profitSharingThreshold: Yup.number().optional().nullable(),
  }),
  // Fuel Config validation
  fuelConfig: Yup.object().shape({
    fuelIncluded: Yup.boolean(),
    fuelRate: Yup.number().when("fuelIncluded", {
      is: true,
      then: (schema) =>
        schema
          .min(0, "Fuel rate must be non-negative")
          .required("Fuel rate is required"),
      otherwise: (schema) => schema.optional(),
    }),
    fuelCity: Yup.string().when("fuelIncluded", {
      is: true,
      then: (schema) => schema.required("Fuel city is required"),
      otherwise: (schema) => schema.optional(),
    }),
    fuelVariance: Yup.string().when("fuelIncluded", {
      is: true,
      then: (schema) => schema.required("Fuel variance is required"),
      otherwise: (schema) => schema.optional(),
    }),
    fuelMileage: Yup.number().when("fuelIncluded", {
      is: true,
      then: (schema) =>
        schema
          .min(0, "Fuel mileage must be non-negative")
          .required("Fuel mileage is required"),
      otherwise: (schema) => schema.optional(),
    }),
  }),
  // Additional Terms validation
  additionalTerms: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required("Type is required"),
      amount: Yup.number()
        .min(0, "Amount must be non-negative")
        .required("Amount is required"),
      remarks: Yup.string().optional(),
    })
  ),
});

const ContractForm: React.FC = () => {
  const { projectId, contractId } = useParams<{
    projectId: string;
    contractId?: string;
  }>();

  const navigate = useNavigate();
  const { showToast } = useToast();
  const [contract, setContract] = useState<FetchedContract | null>(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const isEdit = !!contractId;
  const [searchParams] = useSearchParams();
  const isSuperProject = searchParams.get("isSuperProject") === "true";
  const isDivision = searchParams.get("isDivision") === "true";
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [projectDetails, setProjectDetails] = useState<ProjectModule | null>(
    null
  );

  useEffect(() => {
    fetchConfig();
  }, [contractId, projectId]);

  if (!projectId) {
    showToast("Something went wrong", "error");
    navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`);
    return null;
  }

  const fetchProjectDetails = async () => {
    const data = await projectModuleApi.getModule(projectId);
    setProjectDetails(data);
  };

  // Create dynamic initial values based on mode
  const getInitialValues = (): ContractFormData => {
    if (isEdit && contract) {
      // Return converted contract data for edit mode
      return convertContractToFormData(contract);
    }

    // Return default values for create mode
    return {
      basicInfo: {
        contractNumber: generateContractNumber(projectDetails?.name || ""),
        contractType: ContractType.VEHICLE_ONLY,
        category: Category.SELECT_CATEGORY,
        billingCycle: BillingCycle.MONTHLY,
        status: ContractStatus.DRAFT,
        vehicleCount: undefined,
        resourceCount: undefined,
        startDate:
          new Date(projectDetails?.startDate || "")
            .toISOString()
            .split("T")[0] || new Date().toISOString().split("T")[0],
        endDate: projectDetails?.endDate
          ? new Date(projectDetails?.endDate).toISOString().split("T")[0]
          : "",
        notes: "",
      },
      profitSharing: {
        profitSharingEnabled: false,
        profitCalculationMethod: ProfitCalculationMethod.NET_PROFIT,
      },
      fuelConfig: {
        fuelIncluded: false,
        fuelRate: 0,
        fuelCity: "",
        fuelVariance: 0,
        fuelMileage: 0,
      },
      // Initialize with one resourceTerms for VEHICLE_INCL_DRIVER
      resourceTerms: [
        {
          resourceType: "DRIVER" as any,
          quantity: 1,
          ratePerResource: "",
          rateType: RateType.DAILY,
        },
      ],
      // Initialize fixedTerms contract terms only for FIXED_BILLING category
      fixedTerms: {
        billingCycle: BillingCycle.MONTHLY,
        minGuaranteeAmount: 0,
        distance: 0,
      },
      // fixedBillingTerms: {
      //   billingCycle: BillingCycle.MONTHLY,
      //   fixedBillingTermItems: [],
      // },
      fixedBillingTerms: {
        amount: 0,
        billingType: "",
        billingUnit: 0,
      },
      perKmTerms: {
        kmLimit: 0,
        limitType: LimitType.DAILY,
        ratePerKm: 0,
        overlimitRate: 0,
      },
    };
  };

  // Helper function to safely access nested errors
  const getFieldError = (fieldPath: string, touched: any, errors: any) => {
    const touchedValue = fieldPath
      .split(".")
      .reduce((obj, key) => obj?.[key], touched);
    const errorValue = fieldPath
      .split(".")
      .reduce((obj, key) => obj?.[key], errors);
    return {
      hasError: touchedValue && errorValue,
      errorMessage: errorValue,
    };
  };

  const fetchConfig = async () => {
    setIsLoadingContract(true);
    try {
      fetchProjectDetails();
      if (isEdit) {
        if (!contractId) return;
        const response = await getContractById(contractId);
        if (response.isSuccess && response.data) {
          setContract(response.data as FetchedContract);
        } else {
          showToast(`${ProjectTabEnum.CONTRACT.label} not found`, "error");
          navigate(
            `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=CONTRACT`
          );
        }
      }
    } catch (error) {
      showToast(`Failed to load ${ProjectTabEnum.CONTRACT.label}`, "error");
      navigate(
        `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=CONTRACT`
      );
    } finally {
      setIsLoadingContract(false);
    }
  };

  const handleSubmit = async (
    values: ContractFormData,
    { setSubmitting }: FormikHelpers<ContractFormData>
  ) => {
    try {
      if (isEdit && contract) {
        // Update existing contract - send all data for edit mode
        const updateData: UpdateContractRequest = {
          id: contract.id,
          contractNumber: values.basicInfo?.contractNumber || "",
          contractType: values.basicInfo.contractType,
          category: values.basicInfo.category,
          status: values.basicInfo.status,
          vehicleCount: values.basicInfo.vehicleCount,
          resourceCount: values.basicInfo.resourceCount,
          startDate: values.basicInfo.startDate,
          endDate: values.basicInfo.endDate,
          notes: values.basicInfo.notes,
          perKmTerms: values.perKmTerms,
          perSeatTerms: values.perSeatTerms,
          resourceTerms: values.resourceTerms,
          fixedTerms: values.fixedTerms,
          additionalTerms: values.additionalTerms,
          profitSharing: values.profitSharing,
          fuelConfig: values.fuelConfig,
          // fixedBillingTerms: values.fixedBillingTerms as ContractFixedBilling,
          fixedBillingTerms: values.fixedBillingTerms as ContractFixedBilling,
          revisionDescription: `${ProjectTabEnum.CONTRACT.label} updated`,
        };

        const response = await updateContract(contract.id, updateData);

        if (response.isSuccess) {
          if (selectedFiles && selectedFiles?.length > 0) {
            const uploadPromises = selectedFiles.map((file: File) =>
              uploadFile({
                file: file,
                documentType: "project-contract",
                documentTypeId: response.data?.id || "",
                documentContext: "project",
                description: "Project contract documents",
              })
            );
            const results = await Promise.all(uploadPromises);
          }
          showToast(
            `${ProjectTabEnum.CONTRACT.label} updated successfully`,
            "success"
          );
          if (isSuperProject) {
            navigate(-1);
          } else {
            navigate(
              `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=CONTRACT`
            );
          }
        } else {
          showToast(response.error || "Failed to update contract", "error");
        }
      } else {
        // Create new contract - only send relevant data based on contract type and category
        const { contractType, category } = values.basicInfo;

        const createData: CreateContractRequest = {
          projectId: projectId,
          contractNumber: values.basicInfo?.contractNumber || "",
          contractType: values.basicInfo.contractType,
          category: values.basicInfo.category,
          billingCycle: values.basicInfo.billingCycle,
          vehicleCount: values.basicInfo.vehicleCount,
          resourceCount: values.basicInfo.resourceCount,
          startDate: values.basicInfo.startDate || "",
          endDate: values.basicInfo.endDate,
          notes: values.basicInfo.notes,
          profitSharing: values.profitSharing as ContractProfitSharing,
          fuelConfig: values.fuelConfig as ContractFuelConfig,
        };

        // Only include relevant terms based on contract type and category
        if (
          contractType === ContractType.VEHICLE_ONLY ||
          contractType === ContractType.VEHICLE_INCL_LABOR
        ) {
          if (category === Category.PER_KM && values.perKmTerms) {
            createData.perKmTerms = values.perKmTerms;
          }
          if (category === Category.PER_SEAT && values.perSeatTerms) {
            createData.perSeatTerms = values.perSeatTerms;
          }
          if (category === Category.FIXED && values.fixedTerms) {
            createData.fixedTerms = values.fixedTerms;
          }
        }

        // Include resource terms for VEHICLE_INCL_DRIVER and LABOR_ONLY
        if (
          (contractType === ContractType.VEHICLE_INCL_LABOR ||
            contractType === ContractType.LABOR_ONLY) &&
          values.resourceTerms
        ) {
          createData.resourceTerms = values.resourceTerms;
        }

        // Include additional terms if any
        if (values.additionalTerms && values.additionalTerms.length > 0) {
          createData.additionalTerms = values.additionalTerms;
        }

        // Include fixed billing terms if contract type is vehicle only or vehicle include labor and category is fixed billing
        if (
          (contractType === ContractType.VEHICLE_ONLY ||
            contractType === ContractType.VEHICLE_INCL_LABOR) &&
          category === Category.FIXED_BILLING &&
          values.fixedBillingTerms
        ) {
          createData.fixedBillingTerms =
            values.fixedBillingTerms as ContractFixedBilling;
        }

        const response = await createContract(createData);

        if (response.isSuccess) {
          if (selectedFiles && selectedFiles?.length > 0) {
            const uploadPromises = selectedFiles.map((file: File) =>
              uploadFile({
                file: file,
                documentType: "project-contract",
                documentTypeId: response.data?.id || "",
                documentContext: "project",
                description: "Project contract documents",
              })
            );
            const results = await Promise.all(uploadPromises);
          }
          showToast(
            `${ProjectTabEnum.CONTRACT.label} created successfully`,
            "success"
          );
          if (isSuperProject) {
            navigate(-1);
          } else {
            navigate(
              `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=CONTRACT`
            );
          }
        } else {
          showToast(
            response.error ||
              `Failed to create ${ProjectTabEnum.CONTRACT.label}`,
            "error"
          );
        }
      }
    } catch (error) {
      showToast(
        `An error occurred while ${isEdit ? "updating" : "creating"} the ${
          ProjectTabEnum.CONTRACT.label
        }`,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=CONTRACT`);
  };

  // Convert contract to form data for editing
  const convertContractToFormData = (
    contract: FetchedContract
  ): ContractFormData => {
    return {
      basicInfo: {
        contractNumber: contract.contractNumber,
        contractType: contract.contractType,
        category: contract.category,
        billingCycle: contract.billingCycle,
        status: contract.status,
        startDate: contract.startDate,
        endDate: contract.endDate || "",
        notes: contract.notes || "",
        vehicleCount: contract.vehicleCount || undefined,
        resourceCount: contract.resourceCount || undefined,
      },
      perKmTerms: contract.perKmTerms?.[0]
        ? {
            kmLimit: contract.perKmTerms?.[0].kmLimit,
            limitType: contract.perKmTerms?.[0].limitType,
            ratePerKm: contract.perKmTerms?.[0].ratePerKm,
            overlimitRate: contract.perKmTerms?.[0].overlimitRate,
          }
        : undefined,
      perSeatTerms: contract.perSeatTerms?.[0]
        ? {
            seatCount: contract.perSeatTerms?.[0].seatCount,
            ratePerSeat: contract.perSeatTerms?.[0].ratePerSeat,
            minSeatsGuarantee: contract.perSeatTerms?.[0].minSeatsGuarantee,
            occupancyThreshold: contract.perSeatTerms?.[0].occupancyThreshold,
          }
        : undefined,
      resourceTerms:
        contract.resourceTerms?.map((term) => ({
          resourceType: term.resourceType,
          quantity: term.quantity,
          ratePerResource: term.ratePerResource,
          rateType: term.rateType,
        })) || undefined,
      fixedTerms: contract.fixedTerms?.[0]
        ? {
            billingCycle: contract.fixedTerms?.[0].billingCycle,
            minGuaranteeAmount: contract.fixedTerms?.[0].minGuaranteeAmount,
            distance: contract.fixedTerms?.[0].distance,
          }
        : undefined,
      profitSharing: contract.profitSharing
        ? {
            profitSharingEnabled: contract.profitSharing.profitSharingEnabled,
            profitSharingPercentage:
              contract.profitSharing?.profitSharingPercentage,
            profitSharingThreshold:
              contract.profitSharing?.profitSharingThreshold,
            profitCalculationMethod:
              contract.profitSharing?.profitCalculationMethod,
          }
        : undefined,
      fuelConfig: contract.fuelConfig
        ? {
            fuelIncluded: contract.fuelConfig?.fuelIncluded,
            fuelRate: contract.fuelConfig?.fuelRate,
            fuelCity: contract.fuelConfig?.fuelCity,
            fuelVariance: contract.fuelConfig?.fuelVariance,
            fuelMileage: contract.fuelConfig?.fuelMileage,
          }
        : undefined,
      additionalTerms:
        contract.additionalTerms?.map((term: ContractAdditional) => ({
          type: term.type,
          amount: term.amount,
          remarks: term.remarks,
        })) || [],
      // fixedBillingTerms: contract.fixedBillingTerms
      //   ? {
      //       billingCycle: contract.fixedBillingTerms?.[0]?.billingCycle,
      //       fixedBillingTermItems:
      //         contract.fixedBillingTerms?.[0]?.fixedBillingTermItems,
      //     }
      //   : undefined,
      fixedBillingTerms: contract.fixedBillingTerms?.[0]
        ? {
            amount: contract.fixedBillingTerms?.[0].amount,
            billingType: contract.fixedBillingTerms?.[0].billingType,
            billingUnit: contract.fixedBillingTerms?.[0].billingUnit,
          }
        : undefined,
    };
  };

  if (isLoadingContract || !projectDetails) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Loading message={`Loading...`} fullScreen={false} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title={
          isEdit
            ? `Edit ${ProjectTabEnum.CONTRACT.label}`
            : `Add New ${ProjectTabEnum.CONTRACT.label}`
        }
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: isDivision
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          {
            label: `${PROJECT_TERMINOLOGY.PLURAL} Details`,
            path: `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}${
              isDivision ? "/true" : ""
            }`,
          },
          {
            label: isEdit
              ? `Edit ${ProjectTabEnum.CONTRACT.label}`
              : `Add New ${ProjectTabEnum.CONTRACT.label}`,
          },
        ]}
      />
      {!isSuperProject && (
        <ProjectBasicDetailView projectDetails={projectDetails} />
      )}

      <Box sx={{ mt: 2 }}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            key={isEdit ? contract?.id : "create"}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              isSubmitting,
            }) => {
              console.log(values);
              console.log(errors);
              return (
                <Form>
                  {/* Basic Info Section */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                    >
                      Basic Information
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={2}>
                        <TextField
                          {...commonTextFieldProps}
                          fullWidth
                          label={`${ProjectTabEnum.CONTRACT.label} Number`}
                          name="basicInfo.contractNumber"
                          value={values.basicInfo.contractNumber}
                          onChange={handleChange}
                          error={
                            getFieldError(
                              `basicInfo.contractNumber`,
                              touched,
                              errors
                            ).hasError
                          }
                          helperText={
                            getFieldError(
                              `basicInfo.contractNumber`,
                              touched,
                              errors
                            ).errorMessage
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth {...commonTextFieldProps}>
                          <InputLabel
                            sx={{ fontSize: "0.75rem" }}
                            size="small"
                            required
                          >
                            {ProjectTabEnum.CONTRACT.label} Type
                          </InputLabel>
                          <Select
                            name="basicInfo.contractType"
                            value={values.basicInfo.contractType}
                            onChange={(e) => {
                              const newContractType = e.target
                                .value as ContractType;
                              setFieldValue(
                                "basicInfo.contractType",
                                newContractType
                              );

                              // Auto-set contract type when main contract type changes
                              let newCategory = values.basicInfo.category;
                              const availableTypes =
                                getAvailableContractTypes(newContractType);
                              if (!availableTypes.includes(newCategory)) {
                                newCategory = Category.FIXED_BILLING;
                              }
                              setFieldValue("basicInfo.category", newCategory);

                              // Handle resourceTerms array based on contract type
                              if (newContractType === ContractType.LABOR_ONLY) {
                                setFieldValue("resourceTerms", [
                                  {
                                    resourceType: "DRIVER" as any,
                                    quantity: 1,
                                    ratePerResource: "",
                                    rateType: RateType.DAILY,
                                  },
                                ]);
                              } else if (
                                newContractType === ContractType.VEHICLE_ONLY
                              ) {
                                setFieldValue("resourceTerms", []);
                              } else if (
                                newContractType ===
                                ContractType.VEHICLE_INCL_LABOR
                              ) {
                                if (
                                  !values.resourceTerms ||
                                  values.resourceTerms.length === 0
                                ) {
                                  setFieldValue("resourceTerms", [
                                    {
                                      resourceType: "DRIVER" as any,
                                      quantity: 1,
                                      ratePerResource: "",
                                      rateType: RateType.DAILY,
                                    },
                                  ]);
                                }
                              }
                            }}
                            label={`${ProjectTabEnum.CONTRACT.label} Type`}
                            {...commonTextFieldProps.SelectProps}
                            required
                          >
                            {Object.values(ContractType).map((type) => (
                              <MenuItem
                                key={type}
                                value={type}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth {...commonTextFieldProps}>
                          <InputLabel sx={{ fontSize: "0.75rem" }} required>
                            Billing Cycle
                          </InputLabel>
                          <Select
                            name="basicInfo.billingCycle"
                            value={
                              values.basicInfo.billingCycle ||
                              BillingCycle.MONTHLY
                            }
                            onChange={(e) => handleChange(e as any)}
                            label="Billing Cycle"
                            {...commonTextFieldProps.SelectProps}
                            required
                          >
                            {Object.values(BillingCycle).map((type) => (
                              <MenuItem
                                key={type}
                                value={type}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {(values.basicInfo.contractType ===
                        ContractType.VEHICLE_ONLY ||
                        values.basicInfo.contractType ===
                          ContractType.VEHICLE_INCL_LABOR) && (
                        <Grid item xs={12} md={1.5}>
                          <TextField
                            {...commonTextFieldProps}
                            fullWidth
                            label="Vehicle Count"
                            name="basicInfo.vehicleCount"
                            value={values.basicInfo.vehicleCount}
                            onChange={handleChange}
                            type="number"
                          />
                        </Grid>
                      )}
                      {(values.basicInfo.contractType ===
                        ContractType.VEHICLE_INCL_LABOR ||
                        values.basicInfo.contractType ===
                          ContractType.LABOR_ONLY) && (
                        <Grid item xs={12} md={1.5}>
                          <TextField
                            {...commonTextFieldProps}
                            fullWidth
                            label="WorkForce Count"
                            name="basicInfo.resourceCount"
                            value={values.basicInfo.resourceCount}
                            onChange={handleChange}
                            type="number"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Contract Terms Section */}
                  <Box sx={{ mb: 2 }}>
                    {/* Vehicle + Driver: Show both vehicle and resourceTerms sections */}
                    {values.basicInfo.contractType ===
                      ContractType.VEHICLE_INCL_LABOR && (
                      <>
                        {/* Vehicle Terms Section */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                          >
                            Vehicle Terms
                          </Typography>
                          {renderVehicleTerms(
                            values,
                            setFieldValue,
                            handleChange,
                            touched,
                            errors
                          )}
                        </Box>

                        {/* Resource Terms Section */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                          >
                            WorkForce Terms
                          </Typography>
                          <FieldArray name="resourceTerms">
                            {({ push, remove }) => (
                              <Box>
                                {values.resourceTerms?.map((term, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      mb: 2.5,
                                    }}
                                  >
                                    <Grid container spacing={1.5}>
                                      <Grid item xs={12} md={2}>
                                        <Autocomplete
                                          options={Object.values(ResourceType)}
                                          getOptionLabel={(option) =>
                                            formatLaborType(option)
                                          }
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label="WorkForce Type"
                                              {...commonTextFieldProps}
                                              placeholder="Select or type custom..."
                                              error={
                                                getFieldError(
                                                  `resourceTerms.${index}.resourceType`,
                                                  touched,
                                                  errors
                                                ).hasError
                                              }
                                              helperText={
                                                getFieldError(
                                                  `resourceTerms.${index}.resourceType`,
                                                  touched,
                                                  errors
                                                ).errorMessage
                                              }
                                            />
                                          )}
                                          value={term.resourceType}
                                          onChange={(e, value) =>
                                            setFieldValue(
                                              `resourceTerms.${index}.resourceType`,
                                              value
                                            )
                                          }
                                          freeSolo
                                          selectOnFocus
                                          renderOption={(props, option) => (
                                            <li
                                              {...props}
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              {formatLaborType(option)}
                                            </li>
                                          )}
                                          isOptionEqualToValue={(
                                            option,
                                            value
                                          ) => option === value}
                                        />
                                      </Grid>

                                      <Grid item xs={12} md={1}>
                                        <TextField
                                          {...commonTextFieldProps}
                                          fullWidth
                                          label="Count"
                                          type="number"
                                          name={`resourceTerms.${index}.quantity`}
                                          value={term.quantity}
                                          onChange={handleChange}
                                          error={
                                            getFieldError(
                                              `resourceTerms.${index}.quantity`,
                                              touched,
                                              errors
                                            ).hasError
                                          }
                                          helperText={
                                            getFieldError(
                                              `resourceTerms.${index}.quantity`,
                                              touched,
                                              errors
                                            ).errorMessage
                                          }
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={1.5}>
                                        <FormControl
                                          fullWidth
                                          {...commonTextFieldProps}
                                        >
                                          <InputLabel
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            Rate Type
                                          </InputLabel>
                                          <Select
                                            name={`resourceTerms.${index}.rateType`}
                                            value={term.rateType}
                                            onChange={handleChange}
                                            label="Rate Type"
                                            {...commonTextFieldProps.SelectProps}
                                          >
                                            {Object.values(RateType).map(
                                              (type) => (
                                                <MenuItem
                                                  key={type}
                                                  value={type}
                                                  sx={{ fontSize: "0.75rem" }}
                                                >
                                                  {type}
                                                </MenuItem>
                                              )
                                            )}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={12} md={2}>
                                        <TextField
                                          {...commonTextFieldProps}
                                          fullWidth
                                          label="Rate per WorkForce"
                                          type="number"
                                          name={`resourceTerms.${index}.ratePerResource`}
                                          value={term.ratePerResource}
                                          onChange={handleChange}
                                          error={
                                            getFieldError(
                                              `resourceTerms.${index}.ratePerResource`,
                                              touched,
                                              errors
                                            ).hasError
                                          }
                                          helperText={
                                            getFieldError(
                                              `resourceTerms.${index}.ratePerResource`,
                                              touched,
                                              errors
                                            ).errorMessage
                                          }
                                          InputProps={{
                                            startAdornment: (
                                              <Typography
                                                sx={{
                                                  fontSize: "0.75rem",
                                                  mr: 1,
                                                }}
                                              >
                                                ₹
                                              </Typography>
                                            ),
                                          }}
                                          InputLabelProps={{
                                            shrink: true,
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={2}>
                                        <IconButton
                                          {...smallButtonProp}
                                          color="error"
                                          onClick={() => remove(index)}
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                ))}
                                <Button
                                  {...smallButtonProp}
                                  variant="outlined"
                                  onClick={() =>
                                    push({
                                      resourceType: "DRIVER" as any,
                                      quantity: 1,
                                      ratePerResource: "",
                                      rateType: RateType.DAILY,
                                    })
                                  }
                                >
                                  Add WorkForce
                                </Button>
                              </Box>
                            )}
                          </FieldArray>
                        </Box>
                      </>
                    )}

                    {/* Vehicle Only: Show only vehicle terms */}
                    {values.basicInfo.contractType ===
                      ContractType.VEHICLE_ONLY && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                        >
                          Vehicle Terms
                        </Typography>
                        {renderVehicleTerms(
                          values,
                          setFieldValue,
                          handleChange,
                          touched,
                          errors
                        )}
                      </Box>
                    )}

                    {/* Resource Only: Show only resourceTerms terms */}
                    {values.basicInfo.contractType ===
                      ContractType.LABOR_ONLY && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                        >
                          WorkForce Terms
                        </Typography>
                        <FieldArray name="resourceTerms">
                          {({ push, remove }) => (
                            <Box>
                              {values.resourceTerms?.map((term, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    mb: 2.5,
                                  }}
                                >
                                  <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={2}>
                                      <Autocomplete
                                        options={Object.values(ResourceType)}
                                        getOptionLabel={(option) =>
                                          formatLaborType(option)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label="WorkForce Type"
                                            {...commonTextFieldProps}
                                            placeholder="Select or type custom..."
                                            error={
                                              getFieldError(
                                                `resourceTerms.${index}.resourceType`,
                                                touched,
                                                errors
                                              ).hasError
                                            }
                                            helperText={
                                              getFieldError(
                                                `resourceTerms.${index}.resourceType`,
                                                touched,
                                                errors
                                              ).errorMessage
                                            }
                                          />
                                        )}
                                        value={term.resourceType}
                                        onChange={(e, value) =>
                                          setFieldValue(
                                            `resourceTerms.${index}.resourceType`,
                                            value
                                          )
                                        }
                                        freeSolo
                                        selectOnFocus
                                        renderOption={(props, option) => (
                                          <li
                                            {...props}
                                            style={{ fontSize: "0.75rem" }}
                                          >
                                            {formatLaborType(option)}
                                          </li>
                                        )}
                                        isOptionEqualToValue={(option, value) =>
                                          option === value
                                        }
                                      />
                                    </Grid>

                                    <Grid item xs={12} md={1}>
                                      <TextField
                                        {...commonTextFieldProps}
                                        fullWidth
                                        label="Count"
                                        type="number"
                                        name={`resourceTerms.${index}.quantity`}
                                        value={term.quantity}
                                        onChange={handleChange}
                                        error={
                                          getFieldError(
                                            `resourceTerms.${index}.quantity`,
                                            touched,
                                            errors
                                          ).hasError
                                        }
                                        helperText={
                                          getFieldError(
                                            `resourceTerms.${index}.quantity`,
                                            touched,
                                            errors
                                          ).errorMessage
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={1.5}>
                                      <FormControl
                                        fullWidth
                                        {...commonTextFieldProps}
                                      >
                                        <InputLabel
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          Rate Type
                                        </InputLabel>
                                        <Select
                                          name={`resourceTerms.${index}.rateType`}
                                          value={term.rateType}
                                          onChange={handleChange}
                                          label="Rate Type"
                                          {...commonTextFieldProps.SelectProps}
                                        >
                                          {Object.values(RateType).map(
                                            (type) => (
                                              <MenuItem
                                                key={type}
                                                value={type}
                                                sx={{ fontSize: "0.75rem" }}
                                              >
                                                {type}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                      <TextField
                                        {...commonTextFieldProps}
                                        fullWidth
                                        label="Rate per WorkForce"
                                        type="number"
                                        name={`resourceTerms.${index}.ratePerResource`}
                                        value={term.ratePerResource}
                                        onChange={handleChange}
                                        error={
                                          getFieldError(
                                            `resourceTerms.${index}.ratePerResource`,
                                            touched,
                                            errors
                                          ).hasError
                                        }
                                        helperText={
                                          getFieldError(
                                            `resourceTerms.${index}.ratePerResource`,
                                            touched,
                                            errors
                                          ).errorMessage
                                        }
                                        InputProps={{
                                          startAdornment: (
                                            <Typography
                                              sx={{
                                                fontSize: "0.75rem",
                                                mr: 1,
                                              }}
                                            >
                                              ₹
                                            </Typography>
                                          ),
                                        }}
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={1}>
                                      <IconButton
                                        {...smallButtonProp}
                                        color="error"
                                        onClick={() => remove(index)}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                              <Box>
                                {/* show all resource terms total amount */}
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontSize: "0.8rem", mb: 1 }}
                                >
                                  Total Amount:{" "}
                                  {formatCurrency(
                                    values.resourceTerms?.reduce(
                                      (acc, term) =>
                                        acc +
                                        term.quantity *
                                          Number(term.ratePerResource),
                                      0
                                    )
                                  )}
                                </Typography>
                              </Box>
                              <Button
                                {...smallButtonProp}
                                variant="outlined"
                                onClick={() =>
                                  push({
                                    resourceType: "DRIVER" as any,
                                    quantity: 1,
                                    ratePerResource: "",
                                    rateType: RateType.DAILY,
                                  })
                                }
                              >
                                Add WorkForce
                              </Button>
                            </Box>
                          )}
                        </FieldArray>
                      </Box>
                    )}
                  </Box>

                  {/* Fuel Included Section */}
                  <Box sx={{ mb: 2 }}>
                    <Grid container xs={12} md={12} spacing={1.5}>
                      <Grid item xs={12} md={1.5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="fuelConfig.fuelIncluded"
                              checked={values.fuelConfig?.fuelIncluded || false}
                              onChange={(e) => {
                                setFieldValue(
                                  "fuelConfig.fuelIncluded",
                                  e.target.checked
                                );
                              }}
                              size="small"
                              sx={{
                                "& .MuiCheckbox-root": {
                                  padding: 0,
                                },
                              }}
                            />
                          }
                          label="Fuel Included"
                          sx={{
                            fontSize: "0.75rem",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      </Grid>
                      {values.fuelConfig?.fuelIncluded && (
                        <>
                          <Grid item xs={12} md={1.5}>
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              label="Fuel Rate"
                              type="number"
                              name="fuelConfig.fuelRate"
                              value={values.fuelConfig?.fuelRate || ""}
                              onChange={handleChange}
                              InputProps={{
                                endAdornment: (
                                  <Typography
                                    sx={{ fontSize: "0.75rem", ml: 1 }}
                                  >
                                    ₹/L
                                  </Typography>
                                ),
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              label="Fuel City"
                              name="fuelConfig.fuelCity"
                              value={values.fuelConfig?.fuelCity || ""}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              label="Fuel Variance"
                              name="fuelConfig.fuelVariance"
                              value={values.fuelConfig?.fuelVariance || ""}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              label="Fuel Mileage"
                              name="fuelConfig.fuelMileage"
                              value={values.fuelConfig?.fuelMileage || ""}
                              onChange={handleChange}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>

                  {/* Additional Terms Section */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                    >
                      Additional Terms
                    </Typography>

                    {/* Profit Sharing Section */}
                    <Box sx={{ mb: 2 }}>
                      <Grid container xs={12} md={12} spacing={1.5}>
                        <Grid item xs={12} md={1.5}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="profitSharing.profitSharingEnabled"
                                checked={
                                  values.profitSharing?.profitSharingEnabled ||
                                  false
                                }
                                onChange={handleChange}
                                size="small"
                                sx={{
                                  "& .MuiCheckbox-root": {
                                    padding: 0,
                                  },
                                }}
                              />
                            }
                            label="Profit Sharing"
                            sx={{
                              fontSize: "0.75rem",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </Grid>
                        {values.profitSharing?.profitSharingEnabled && (
                          <>
                            <Grid item xs={12} md={2}>
                              <FormControl fullWidth {...commonTextFieldProps}>
                                <InputLabel sx={{ fontSize: "0.75rem" }}>
                                  Profit Calculation Method
                                </InputLabel>
                                <Select
                                  name="profitSharing.profitCalculationMethod"
                                  value={
                                    values.profitSharing
                                      ?.profitCalculationMethod ||
                                    ProfitCalculationMethod.NET_PROFIT
                                  }
                                  onChange={(e) => handleChange(e as any)}
                                  label="Profit Calculation Method"
                                  {...commonTextFieldProps.SelectProps}
                                >
                                  {Object.values(ProfitCalculationMethod).map(
                                    (type) => (
                                      <MenuItem
                                        key={type}
                                        value={type}
                                        sx={{ fontSize: "0.75rem" }}
                                      >
                                        {type}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={1.5}>
                              <TextField
                                {...commonTextFieldProps}
                                fullWidth
                                label="Profit Sharing Percentage"
                                type="number"
                                name="profitSharing.profitSharingPercentage"
                                value={
                                  values.profitSharing
                                    ?.profitSharingPercentage || ""
                                }
                                onChange={handleChange}
                                InputProps={{
                                  endAdornment: (
                                    <Typography
                                      sx={{ fontSize: "0.75rem", ml: 1 }}
                                    >
                                      %
                                    </Typography>
                                  ),
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                    <FieldArray name="additionalTerms">
                      {({ push, remove }) => (
                        <Box>
                          <Grid container spacing={1.5} mb={1}>
                            {values.additionalTerms?.map((term, index) => (
                              <Grid
                                container
                                item
                                xs={12}
                                md={12}
                                key={index}
                                spacing={1.5}
                              >
                                <Grid item xs={12} md={2}>
                                  <Autocomplete
                                    options={Object.values(AdditionalType)}
                                    getOptionLabel={(option) => option}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Type"
                                        {...commonTextFieldProps}
                                        placeholder="Select or type custom..."
                                        error={
                                          getFieldError(
                                            `additionalTerms.${index}.type`,
                                            touched,
                                            errors
                                          ).hasError
                                        }
                                        helperText={
                                          getFieldError(
                                            `additionalTerms.${index}.type`,
                                            touched,
                                            errors
                                          ).errorMessage
                                        }
                                      />
                                    )}
                                    value={term.type}
                                    onChange={(e, value) =>
                                      setFieldValue(
                                        `additionalTerms.${index}.type`,
                                        value
                                      )
                                    }
                                    freeSolo
                                    selectOnFocus
                                    renderOption={(props, option) => (
                                      <li
                                        {...props}
                                        style={{ fontSize: "0.75rem" }}
                                      >
                                        {option}
                                      </li>
                                    )}
                                    isOptionEqualToValue={(option, value) =>
                                      option === value
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    {...commonTextFieldProps}
                                    fullWidth
                                    label="Amount"
                                    name={`additionalTerms.${index}.amount`}
                                    value={term.amount || ""}
                                    onChange={handleChange}
                                    error={
                                      getFieldError(
                                        `additionalTerms.${index}.amount`,
                                        touched,
                                        errors
                                      ).hasError
                                    }
                                    helperText={
                                      getFieldError(
                                        `additionalTerms.${index}.amount`,
                                        touched,
                                        errors
                                      ).errorMessage
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <Typography
                                          sx={{ fontSize: "0.75rem", mr: 1 }}
                                        >
                                          ₹
                                        </Typography>
                                      ),
                                    }}
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    {...commonTextFieldProps}
                                    fullWidth
                                    label="Remarks"
                                    name={`additionalTerms.${index}.remarks`}
                                    value={term.remarks || ""}
                                    onChange={handleChange}
                                  />
                                </Grid>
                                <Grid item xs={2}>
                                  <IconButton
                                    {...smallButtonProp}
                                    color="error"
                                    onClick={() => remove(index)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            ))}
                          </Grid>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              push({ type: "", amount: 0, remarks: "" })
                            }
                            {...smallButtonProp}
                          >
                            Add Additional Term
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>

                  <Grid item xs={12} mb={2}>
                    <TextField
                      {...commonTextFieldProps}
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      name="basicInfo.notes"
                      value={values.basicInfo.notes || ""}
                      onChange={handleChange}
                    />
                  </Grid>

                  <DocumentUploadSection
                    title={`Upload ${ProjectTabEnum.CONTRACT.label}`}
                    selectedFiles={selectedFiles}
                    uploadedFiles={contract?.documents || []}
                    onFileSelect={(files: File[]) =>
                      setSelectedFiles((prev: File[]) => [...prev, ...files])
                    }
                    onFileRemove={(index: number) =>
                      setSelectedFiles((prev: File[]) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    onPreview={(url) => {
                      window.open(url, "_blank");
                    }}
                    isLoading={false}
                    uploadedTitle={`Uploaded ${ProjectTabEnum.CONTRACT.label} Documents`}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      {...commonButtonProps}
                    >
                      Cancel
                    </Button>
                    <Button
                      {...commonButtonProps}
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? isEdit
                          ? "Updating..."
                          : "Creating..."
                        : isEdit
                        ? "Update"
                        : "Create"}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Paper>
      </Box>
    </Container>
  );
};

// export const renderFixedBillingItems = (
//   index: number,
//   values: ContractFormData,
//   handleChange: (e: React.ChangeEvent<any>) => void,
//   removeItem: (index: number) => void,
//   setFieldValue: (field: string, value: any) => void
// ) => {
//   return (
//     <Grid container spacing={1.5}>
//       <Grid item xs={12} md={2}>
//         <FormControl fullWidth {...commonTextFieldProps}>
//           <InputLabel sx={{ fontSize: "0.75rem" }}>Vehicle Category</InputLabel>
//           <Select
//             name={`fixedBillingTerms.fixedBillingTermItems.${index}.vehicleCategory`}
//             value={
//               values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//                 ?.vehicleCategory || ""
//             }
//             onChange={(e) => handleChange(e as any)}
//             label={`Vehicle Category`}
//             {...commonTextFieldProps.SelectProps}
//           >
//             {Object.values(VehicleCategory).map((type: VehicleCategory) => (
//               <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
//                 {type}
//               </MenuItem>
//             ))}
//           </Select>
//         </FormControl>
//       </Grid>
//       <Grid item xs={12} md={2}>
//         <FormControl fullWidth {...commonTextFieldProps}>
//           <InputLabel sx={{ fontSize: "0.75rem" }}>Vehicle Make</InputLabel>
//           <Select
//             name={`fixedBillingTerms.fixedBillingTermItems.${index}.vehicleMake`}
//             value={
//               values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//                 ?.vehicleMake || ""
//             }
//             onChange={(e) => handleChange(e as any)}
//             label={`Vehicle Make`}
//             {...commonTextFieldProps.SelectProps}
//           >
//             {getVehicleMakes(
//               values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//                 ?.vehicleCategory as VehicleCategory
//             )?.map((type: string) => (
//               <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
//                 {type}
//               </MenuItem>
//             ))}
//           </Select>
//         </FormControl>
//       </Grid>
//       <Grid item xs={12} md={1.5}>
//         <FormControl fullWidth {...commonTextFieldProps}>
//           <InputLabel sx={{ fontSize: "0.75rem" }}>Vehicle Model</InputLabel>
//           <Select
//             name={`fixedBillingTerms.fixedBillingTermItems.${index}.vehicleModel`}
//             value={
//               values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//                 ?.vehicleModel || ""
//             }
//             onChange={(e) => handleChange(e as any)}
//             label={`Vehicle Model`}
//             {...commonTextFieldProps.SelectProps}
//           >
//             {getVehicleModels(
//               values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//                 ?.vehicleCategory as VehicleCategory
//             )?.map((type: string) => (
//               <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
//                 {type}
//               </MenuItem>
//             ))}
//           </Select>
//         </FormControl>
//       </Grid>
//       {/* <Grid item xs={12} md={2}>
//         <TextField
//           {...commonTextFieldProps}
//           fullWidth
//           label="Pricing Type"
//           name={`fixedBillingTerms.fixedBillingTermItems.${index}.pricingType`}
//           value={values.fixedBillingTerms?.fixedBillingTermItems?.[index]?.pricingType || ""}
//           onChange={handleChange}
//         />
//       </Grid> */}
//       <Grid item xs={12} md={2}>
//         <TextField
//           {...commonTextFieldProps}
//           fullWidth
//           label="Amount"
//           name={`fixedBillingTerms.fixedBillingTermItems.${index}.pricingValue`}
//           value={
//             values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//               ?.pricingValue &&
//             values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//               ?.pricingValue > 0
//               ? Number(
//                   values.fixedBillingTerms?.fixedBillingTermItems?.[index]
//                     ?.pricingValue
//                 ).toLocaleString("en-IN")
//               : ""
//           }
//           InputProps={{
//             startAdornment: (
//               <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>₹</Typography>
//             ),
//           }}
//           onChange={(e) => {
//             const value = e.target.value.replace(/,/g, "");
//             handleChange(e);
//             if (value && !isNaN(Number(value))) {
//               setFieldValue(
//                 `fixedBillingTerms.fixedBillingTermItems.${index}.pricingValue`,
//                 value
//               );
//             }
//           }}
//         />
//       </Grid>
//       {values.fixedBillingTerms?.fixedBillingTermItems &&
//         values.fixedBillingTerms?.fixedBillingTermItems?.length > 1 && (
//           <Grid item xs={12} md={2}>
//             <IconButton
//               {...smallButtonProp}
//               color="error"
//               onClick={() => removeItem(index)}
//             >
//               <Delete />
//             </IconButton>
//           </Grid>
//         )}
//     </Grid>
//   );
// };

// Helper function to render vehicle terms

export const renderVehicleTerms = (
  values: ContractFormData,
  setFieldValue: (field: string, value: any) => void,
  handleChange: (e: React.ChangeEvent<any>) => void,
  touched: any,
  errors: any
) => {
  const handleCategoryChange = (e: SelectChangeEvent<Category>) => {
    handleChange(e as any);
    if (e.target.value === Category.FIXED_BILLING) {
      setFieldValue("fixedBillingTerms.fixedBillingTermItems", [
        {
          vehicleCategory: VehicleCategory.AMBULANCE,
          vehicleMake: "Force",
          vehicleModel: "ALSA",
          pricingType: PricingType.FIXED,
          pricingValue: "",
        },
      ]);
    }
  };
  const getBillingTypes = (billingCycle: BillingCycle) => {
    switch (billingCycle) {
      case BillingCycle.DAILY:
        return [BillingType.KM, BillingType.TRIP, BillingType.DAYS];
      case BillingCycle.MONTHLY:
        return [
          BillingType.KM,
          BillingType.TRIP,
          BillingType.DAYS,
          BillingType.MONTHS,
        ];
      case BillingCycle.QUARTERLY:
      case BillingCycle.HALF_YEARLY:
      case BillingCycle.YEARLY:
        return [
          BillingType.KM,
          BillingType.TRIP,
          BillingType.DAYS,
          BillingType.MONTHS,
        ];
      case BillingCycle.ONE_TIME:
        return [
          BillingType.KM,
          BillingType.TRIP,
          BillingType.DAYS,
          BillingType.MONTHS,
          BillingType.YEARS,
        ];
      default:
        return [BillingType.KM, BillingType.TRIP, BillingType.DAYS];
    }
  };

  // const handleBillingUnitChange = (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   handleChange: (e: React.ChangeEvent<any>) => void
  // ) => {
  //   const { value, name } = e.target;
  //   const numericValue = Number(value);

  //   let max = 1;

  //   const billingCycle = values.basicInfo.billingCycle;
  //   const billingType = values.fixedBillingTerms?.billingType;

  //   if (
  //     billingCycle === BillingCycle.DAILY &&
  //     billingType === BillingType.DAYS
  //   ) {
  //     max = 1;
  //   } else if (
  //     billingCycle === BillingCycle.MONTHLY &&
  //     billingType === BillingType.MONTHS
  //   ) {
  //     max = 1;
  //   } else if (
  //     billingCycle === BillingCycle.QUARTERLY &&
  //     billingType === BillingType.MONTHS
  //   ) {
  //     max = 3;
  //   } else if (
  //     billingCycle === BillingCycle.HALF_YEARLY &&
  //     billingType === BillingType.MONTHS
  //   ) {
  //     max = 6;
  //   } else if (
  //     (billingCycle === BillingCycle.YEARLY ||
  //       billingCycle === BillingCycle.ONE_TIME) &&
  //     billingType === BillingType.MONTHS
  //   ) {
  //     max = 12;
  //   }

  //   if (numericValue <= max && numericValue >= 1) {
  //     handleChange(e);
  //   }
  // };

  return (
    <Box>
      <Box>
        <Grid container spacing={1.5}>
          {values.basicInfo.category !== Category.FIXED_BILLING && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth {...commonTextFieldProps}>
                <InputLabel sx={{ fontSize: "0.75rem" }}>
                  Vehicle {ProjectTabEnum.CONTRACT.label} Type
                </InputLabel>
                <Select
                  name="basicInfo.category"
                  value={values.basicInfo.category}
                  onChange={handleCategoryChange}
                  label={`Vehicle ${ProjectTabEnum.CONTRACT.label} Type`}
                  {...commonTextFieldProps.SelectProps}
                >
                  {getAvailableContractTypes(values.basicInfo.contractType).map(
                    (type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          {values.basicInfo.category === Category.ROUTE_BASED && (
            <Grid item xs={12} md={10}>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <InfoOutlined sx={{ fontSize: "1.2rem", mr: 1 }} color="info" />
                <Typography variant="subtitle2" sx={{ fontSize: "0.8rem" }}>
                  Your Billing will happen based on the Route uses.
                </Typography>
              </Box>
            </Grid>
          )}

          {values.basicInfo.category === Category.FIXED && (
            <Grid container item md={10} spacing={1.5}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Frequency
                  </InputLabel>
                  <Select
                    name="fixedTerms.billingCycle"
                    value={
                      values.fixedTerms?.billingCycle || BillingCycle.MONTHLY
                    }
                    onChange={(e) => handleChange(e as any)}
                    label="Billing Cycle"
                    {...commonTextFieldProps.SelectProps}
                  >
                    {Object.values(BillingCycle).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Minimum Guarantee Amount"
                  type="number"
                  name="fixedTerms.minGuaranteeAmount"
                  value={values.fixedTerms?.minGuaranteeAmount || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Distance"
                  type="number"
                  name="fixedTerms.distance"
                  value={values.fixedTerms?.distance || ""}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", ml: 1 }}>
                        km
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {values.basicInfo.category === Category.PER_KM && (
            <Grid container item md={10} spacing={1.5}>
              <Grid item xs={12} md={1.5}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Limit Type
                  </InputLabel>
                  <Select
                    name="perKmTerms.limitType"
                    value={values.perKmTerms?.limitType || LimitType.DAILY}
                    onChange={(e) => handleChange(e as any)}
                    label="Limit Type"
                    {...commonTextFieldProps.SelectProps}
                  >
                    {Object.values(LimitType).map((type: LimitType) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="KM Limit"
                  type="number"
                  name="perKmTerms.kmLimit"
                  value={values.perKmTerms?.kmLimit || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Rate per KM"
                  type="number"
                  name="perKmTerms.ratePerKm"
                  value={values.perKmTerms?.ratePerKm || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Over-limit Rate"
                  type="number"
                  name="perKmTerms.overlimitRate"
                  value={values.perKmTerms?.overlimitRate || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {values.basicInfo.category === Category.PER_SEAT && (
            <Grid container item md={10} spacing={1.5}>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Seat Count"
                  type="number"
                  name="perSeatTerms.seatCount"
                  value={values.perSeatTerms?.seatCount || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Rate per Seat"
                  type="number"
                  name="perSeatTerms.ratePerSeat"
                  value={values.perSeatTerms?.ratePerSeat || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Minimum Seats Guarantee"
                  type="number"
                  name="perSeatTerms.minSeatsGuarantee"
                  value={values.perSeatTerms?.minSeatsGuarantee || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {values.basicInfo.category === Category.FIXED_BILLING && (
            <Grid container item md={12} spacing={1.5}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Vehicle {ProjectTabEnum.CONTRACT.label} Type
                  </InputLabel>
                  <Select
                    name="basicInfo.category"
                    value={values.basicInfo.category}
                    onChange={handleCategoryChange}
                    label={`Vehicle ${ProjectTabEnum.CONTRACT.label} Type`}
                    {...commonTextFieldProps.SelectProps}
                  >
                    {getAvailableContractTypes(
                      values.basicInfo.contractType
                    ).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Amount"
                  type="number"
                  name="fixedBillingTerms.amount"
                  value={values.fixedBillingTerms?.amount || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Billing Type
                  </InputLabel>
                  <Select
                    name="fixedBillingTerms.billingType"
                    value={values.fixedBillingTerms?.billingType || ""}
                    onChange={(e) => handleChange(e as any)}
                    label="Billing Type"
                    {...commonTextFieldProps.SelectProps}
                  >
                    {Object.values(
                      getBillingTypes(values.basicInfo.billingCycle)
                    ).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  type="decimal"
                  label="Billing Unit"
                  name="fixedBillingTerms.billingUnit"
                  value={values.fixedBillingTerms?.billingUnit || ""}
                  onChange={handleChange}
                  placeholder="Ex: 50Km, 1Day, 1Trip, 1Month"
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", ml: 1 }}>
                        {values.fixedBillingTerms?.billingType}
                      </Typography>
                    ),
                  }}
                  inputProps={{
                    min: 1,
                  }}
                />
              </Grid>
              {/* <Grid item xs={12} md={12}>
                <FieldArray name="fixedBillingTerms.fixedBillingTermItems">
                  {({ push, remove }) => (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                      >
                        Vehicle List
                      </Typography>
                      {values.fixedBillingTerms?.fixedBillingTermItems?.map(
                        (item, index) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            {renderFixedBillingItems(
                              index,
                              values,
                              handleChange,
                              remove,
                              setFieldValue
                            )}
                          </Box>
                        )
                      )}
                      <Button
                        {...smallButtonProp}
                        variant="outlined"
                        onClick={() =>
                          push({
                            vehicleCategory: VehicleCategory.AMBULANCE,
                            vehicleMake: "Force",
                            vehicleModel: "ALSA",
                            pricingType: PricingType.FIXED,
                            pricingValue: "",
                          })
                        }
                      >
                        Add Vehicle
                      </Button>
                    </Box>
                  )}
                </FieldArray>
              </Grid> */}
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default ContractForm;
